/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { RefObject } from 'preact';
import { useRef, useCallback, useState, useImperativeHandle } from 'preact/hooks';
import { classNames } from '../utils/UNSAFE_classNames';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { containsKey, Keys, isKeyDefined } from '../utils/UNSAFE_keys';
import { DataState, ItemContext, ListItemRendererContext, Range } from '../UNSAFE_Collection';
import { LoadMoreCollection, LOADMORE_STYLE_CLASS } from '../PRIVATE_LoadMoreCollection';
import { VirtualizedCollection } from '../PRIVATE_VirtualizedCollection';
import { ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';
import { ListViewItem, ITEM_SELECTOR, MemoizeListViewItem } from './ListItem';

import { SelectionRangeDetail } from '../hooks/PRIVATE_useSelection';
import { SkeletonContainer } from './SkeletonContainer';
import { Skeleton } from '../UNSAFE_Skeleton';
import { Flex } from '../UNSAFE_Flex';
import { Selector } from '../UNSAFE_Selector';
import { Props } from '../UNSAFE_ListView';
import { Sparkle } from './Sparkle';
import { listStyles } from './themes/ListStyles.css';
import { useTestId } from '../hooks/UNSAFE_useTestId';
import { usePromotedSection } from './usePromotedSection';
import { useSelectionAndNavigation, handleSelectionRange } from './useSelectionAndNavigation';
import { useFocusTabbableMode } from './useFocusTabbableMode';
import { Inset } from '../UNSAFE_Inset';
// TODO: add this import due to issue JET-67485, need to update once Stable is introduced
import './themes/redwood/ListBaseTheme.css';

/**
 * Component that renders items as a flat list.
 * In order to maximize performance, only items that are visible in the viewport are rendered.
 */
export function List<K extends string | number, D>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  allowTabbableMode = true,
  children,
  currentKey,
  data,
  gridlines,
  loadingIndicator = defaultLoadingIndicator,
  onCurrentKeyChange,
  onLoadRange,
  onSelectionChange,
  onItemAction,
  rangeExtractor,
  role = 'grid',
  selectedKeys = emptyKeys as Keys<K>,
  selectionMode = 'none',
  viewportConfig,
  currentItemVariant = 'none',
  promotedSection,
  isVirtualized = false,
  customItemRenderer,
  scrollToVisibleOffset,
  testId,
  scrollerRef,
  reorderProps,
  contextMenuTriggerProps,
  ...props
}: Props<K, D>) {
  const rootRef = useRef<HTMLDivElement>(null);

  // tracking pending selection key that is not in current data
  const pendingSelection = useRef<{ detail: SelectionRangeDetail<K>; range: Range }>();

  const [active, setActive] = useState<boolean>(false);

  const showGridline = (index: number) => {
    // show bottom gridlines for each item, and for the last item if specified
    return (
      gridlines?.item === 'visible' &&
      (index + 1 !== data?.totalSize || gridlines?.bottom === 'visible')
    );
  };

  const showGridlineTop = () => {
    // show the top gridline for the first item
    return gridlines?.item === 'visible' && gridlines?.top === 'visible';
  };

  const ariaMultiSelectable = selectionMode === 'none' ? undefined : selectionMode === 'multiple';
  const ariaRowCount =
    data === null ? undefined : data.sizePrecision === 'exact' ? data.totalSize : -1;

  const classes = classNames([listStyles.base]);

  const pendingSelectionCallback = useCallback(
    (detail: SelectionRangeDetail<K>, value: Range) => {
      if (data) {
        pendingSelection.current = {
          detail: detail,
          range: { offset: data.offset, count: data.data.length }
        };
        onLoadRange(value);
      }
    },
    [data, onLoadRange]
  );
  const { eventProps, showFocusRing, handleSelectionChange } = useSelectionAndNavigation(
    currentKey,
    selectedKeys,
    selectionMode,
    onCurrentKeyChange,
    onSelectionChange,
    onItemAction,
    data,
    viewportConfig,
    role,
    scrollToVisibleOffset,
    rootRef,
    pendingSelectionCallback
  );

  const { eventProps: focusTabbableProps, isCurrentTabbableKey } = useFocusTabbableMode(
    currentKey,
    onCurrentKeyChange,
    allowTabbableMode,
    rootRef,
    viewportConfig,
    scrollToVisibleOffset
  );

  if (data && pendingSelection.current !== undefined && onSelectionChange) {
    const keys = handleSelectionRange(pendingSelection.current.detail, data);
    if (Array.isArray(keys)) {
      onSelectionChange({ value: { all: false, keys: new Set(keys) }, target: null });
    }
    const range = pendingSelection.current
      ? pendingSelection.current.range
      : { offset: data.offset, count: data.data.length };
    data = adjustDataState(data, range);
    pendingSelection.current = undefined;
    onLoadRange(range);
  }

  const { sparkleHeight, suggestions } = usePromotedSection(data, promotedSection, rootRef);

  // TODO: check suggestions before Sparkle would cause a scrolling issue
  const sparkleIndicator = <Sparkle sparkleHeight={sparkleHeight}></Sparkle>;

  // Collection generic is of this type
  type DataStateData = DataState<K, D>['data'][number];

  // returns an ListItemContext based on ItemContext (added metadata and selector)
  // note the type for ItemContext, this is the context coming from Collection
  const getItemContext = useCallback(
    (context: ItemContext<DataStateData>) => {
      const dataStateData = context.data;
      const selectorRenderer =
        selectionMode === 'multiple'
          ? () => (
              <Selector
                onChange={handleSelectionChange}
                rowKey={dataStateData.metadata.key}
                selectedKeys={selectedKeys}></Selector>
            )
          : undefined;

      return {
        index: context.index,
        data: dataStateData.data,
        metadata: dataStateData.metadata,
        selector: selectorRenderer
      };
    },
    [selectionMode, selectedKeys, handleSelectionChange]
  );

  const ItemComponent = isVirtualized ? ListViewItem : MemoizeListViewItem;

  const childrenComponent = (context: ItemContext<DataStateData>) => {
    const listItemContext = getItemContext(context);
    const suggestion = suggestions?.get(listItemContext.metadata.key);
    const isTabbable = allowTabbableMode && isCurrentTabbableKey(listItemContext.metadata.key);
    const isFocused = currentKey === listItemContext.metadata.key && !isTabbable;
    const isActive = isFocused && active;
    const isSelected = containsKey(selectedKeys, listItemContext.metadata.key);
    const isFocusRingVisible = isFocused && showFocusRing;
    const listItemFunc = (ctx: ListItemRendererContext<K, D>, otherProps?: any) => (
      <ItemComponent
        key={listItemContext.metadata.key}
        context={ctx}
        isFocused={isFocused}
        isFocusRingVisible={isFocusRingVisible}
        isActive={isActive}
        isGridlineVisible={showGridline(listItemContext.index)}
        isSelected={isSelected}
        isTabbable={allowTabbableMode && isCurrentTabbableKey(listItemContext.metadata.key)}
        role={role}
        selectionMode={selectionMode}
        currentItemVariant={currentItemVariant}
        {...(suggestion && { suggestion })}
        {...(listItemContext.index === 0 && { isTopGridlineVisible: showGridlineTop() })}
        {...otherProps}>
        {children}
      </ItemComponent>
    );

    // if customItemRenderer is specified (ExpandableList and GroupedList)
    // create a custom context with additional info including a function
    // that helps create the default item component
    if (customItemRenderer) {
      const customRendererContext = {
        listItemContext,
        isFocused,
        isFocusRingVisible,
        isSelected,
        isTabbable,
        isActive,
        defaultListItem: listItemFunc
      };
      return customItemRenderer(customRendererContext);
    }
    return listItemFunc(listItemContext);
  };

  viewportConfig = getViewportConfig(rootRef, viewportConfig);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === ' ' && isKeyDefined(currentKey) && selectionMode !== 'none') {
        setActive(true);
      }
    },
    [currentKey, selectionMode, setActive]
  );

  const handleKeyUp = useCallback(() => {
    setActive(false);
  }, [setActive]);

  useImperativeHandle(scrollerRef!, () => rootRef.current);

  // if data is not specified, listview should show loading indicator
  // todo: replace placeholder with actual SkeletonContainer component (Ash is working on)
  const testIdProps = useTestId(testId);
  return (
    <div
      {...mergeProps(
        eventProps,
        focusTabbableProps,
        contextMenuTriggerProps || {},
        reorderProps || {},
        {
          onKeyDown: handleKeyDown,
          onKeyUp: handleKeyUp
        },
        props
      )}
      {...testIdProps}
      role={role}
      aria-rowcount={role === 'grid' ? ariaRowCount : undefined}
      aria-colcount={role === 'grid' ? 1 : undefined}
      ref={rootRef}
      class={classes}
      tabIndex={0}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-multiselectable={ariaMultiSelectable}>
      {data == null ? (
        loadingIndicator
      ) : isVirtualized ? (
        <VirtualizedCollection
          data={data}
          itemSelector={ITEM_SELECTOR}
          loadMoreIndicator={defaultLoadMoreIndicator}
          onLoadRange={onLoadRange}
          rangeExtractor={rangeExtractor}
          suggestions={sparkleIndicator}
          viewportConfig={viewportConfig}>
          {childrenComponent}
        </VirtualizedCollection>
      ) : (
        <LoadMoreCollection
          data={data.data}
          loadMoreIndicator={defaultLoadMoreIndicator}
          hasMore={data.sizePrecision === 'atLeast' && data.data.length <= data.totalSize}
          onLoadMore={() => {
            data && onLoadRange({ offset: 0, count: data.data.length + 25 });
          }}
          suggestions={sparkleIndicator}
          viewportConfig={viewportConfig}>
          {childrenComponent}
        </LoadMoreCollection>
      )}
    </div>
  );
}

const getViewportConfig = (rootRef: RefObject<HTMLElement>, config?: ViewportConfig) => {
  return (
    config ?? {
      scroller: () => {
        return rootRef.current;
      }
    }
  );
};

const emptyKeys = { all: false, keys: new Set() };

const defaultLoadingIndicator = (
  <SkeletonContainer minimumCount={25}>
    {() => {
      return (
        <Inset variant="listview">
          <Flex height="6x" align="center">
            <Skeleton height="4x" />
          </Flex>
        </Inset>
      );
    }}
  </SkeletonContainer>
);

const defaultLoadMoreIndicator = (
  <div class={LOADMORE_STYLE_CLASS}>
    <SkeletonContainer minimumCount={3}>
      {() => {
        return (
          <Inset variant="listview">
            <Flex height="6x" align="center">
              <Skeleton height="4x" />
            </Flex>
          </Inset>
        );
      }}
    </SkeletonContainer>
  </div>
);

/**
 * Adjust the DataState as needed if it contains more than needed for the specified range
 */
const adjustDataState = <K, D>(dataState: DataState<K, D>, range: Range | null) => {
  if (range) {
    // prevent range offset from being larger than the data state
    const safeRangeOffset = Math.min(range.offset, dataState.offset + dataState.totalSize);
    const diff = safeRangeOffset - dataState.offset;
    if (diff > 0) {
      dataState = {
        offset: safeRangeOffset,
        data: dataState.data.slice(diff, diff + range.count),
        totalSize: dataState.totalSize,
        sizePrecision: dataState.sizePrecision
      };
    }
  }
  return dataState;
};
